import React, { useEffect, useState } from "react";
import { X, FileSearch, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { createLead } from "@/lib/api";
import { openExternal } from "@/lib/openExternal";

const CHECKMYFILE_URL = "https://www.checkmyfile.partners/H4CRD56/2CTPL/";

const GOALS = [
    "First-time buyer",
    "Moving home",
    "Remortgage",
    "Buy-to-let / landlord",
    "Commercial / business finance",
    "Protection & insurance",
    "General enquiry",
];

const TIMELINES = [
    { value: "asap", label: "As soon as possible" },
    { value: "1_month", label: "Within 1 month" },
    { value: "2_months", label: "Within 2 months" },
    { value: "3_6_months", label: "3–6 months" },
    { value: "exploring", label: "Just exploring" },
];

/**
 * Pre-form gate before sending visitors to the CheckMyFile partner link.
 * Collects lead details (name, phone, email, goal, borrow amount) first, then
 * fires the lead + redirects so every credit-report handoff is tracked.
 */
export default function CheckMyFileModal({ open, onClose }) {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        goal: "",
        amount: "",
        timeline: "",
    });
    const [loading, setLoading] = useState(false);

    // Close on ESC + lock body scroll while open
    useEffect(() => {
        if (!open) return;
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = prev;
        };
    }, [open, onClose]);

    if (!open) return null;

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.phone) {
            toast.error("Please add your name, email and phone number.");
            return;
        }
        if (!form.goal) {
            toast.error("Please pick what you're looking to do.");
            return;
        }
        setLoading(true);
        try {
            const amountNum = form.amount
                ? parseFloat(form.amount.replace(/[^0-9.]/g, ""))
                : null;
            await createLead({
                name: form.name,
                email: form.email,
                phone: form.phone || null,
                service: form.goal,
                goal: form.goal,
                property_value: amountNum,
                timeline: form.timeline || null,
                source: "check_my_file",
                message: `CheckMyFile gate · ${form.goal}${
                    amountNum ? ` · Looking to borrow £${amountNum.toLocaleString()}` : ""
                }${form.timeline ? ` · Timeline: ${form.timeline}` : ""}`,
            });
            toast.success("Thanks — opening your free credit report now.");
            openExternal(CHECKMYFILE_URL);
            onClose();
        } catch {
            toast.error("Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            data-testid="cmf-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="cmf-title"
            className="fixed inset-0 z-[100] flex items-center justify-center p-4"
        >
            {/* Backdrop */}
            <div
                onClick={onClose}
                aria-hidden="true"
                className="absolute inset-0 bg-black/55 backdrop-blur-sm"
            />

            {/* Dialog */}
            <div className="relative w-full max-w-md bg-brand-surface border-2 border-brand-border rounded-2xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.45)] overflow-hidden">
                <div className="bg-brand-primary text-white px-6 py-5 flex items-start justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="h-11 w-11 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
                            <FileSearch size={22} />
                        </div>
                        <div>
                            <h2 id="cmf-title" className="font-black text-xl leading-tight">
                                Free credit report
                            </h2>
                            <p className="text-[11px] uppercase tracking-[0.2em] text-white/80 font-bold mt-1">
                                Check my file · 30-second form
                            </p>
                        </div>
                    </div>
                    <button
                        data-testid="cmf-close"
                        onClick={onClose}
                        aria-label="Close"
                        className="text-white/80 hover:text-white shrink-0"
                    >
                        <X size={20} />
                    </button>
                </div>

                <form
                    data-testid="cmf-form"
                    onSubmit={submit}
                    className="px-6 py-6 space-y-4 max-h-[70vh] overflow-y-auto"
                >
                    <p className="text-sm text-brand-muted leading-relaxed">
                        Share a few quick details so an Acumen adviser can follow up
                        after you check your file. Your free report opens in a new tab
                        — it's a soft credit check that won't affect your score.
                    </p>

                    <Field
                        label="Full name"
                        required
                        value={form.name}
                        onChange={(v) => set("name", v)}
                        testid="cmf-name"
                        autoComplete="name"
                    />
                    <Field
                        label="Email"
                        type="email"
                        required
                        value={form.email}
                        onChange={(v) => set("email", v)}
                        testid="cmf-email"
                        autoComplete="email"
                    />
                    <Field
                        label="Phone"
                        type="tel"
                        required
                        value={form.phone}
                        onChange={(v) => set("phone", v)}
                        testid="cmf-phone"
                        autoComplete="tel"
                        placeholder="For a quick call-back"
                    />

                    <div>
                        <label className="text-[11px] uppercase tracking-[0.2em] text-brand-muted font-bold">
                            What are you looking to do? <span className="text-brand-accent">*</span>
                        </label>
                        <Select
                            value={form.goal}
                            onValueChange={(v) => set("goal", v)}
                        >
                            <SelectTrigger
                                data-testid="cmf-goal"
                                className="mt-1.5 w-full border-0 border-b border-brand-border bg-transparent rounded-none px-0 py-3 text-base text-brand-primary focus:ring-0 focus:border-brand-primary h-auto"
                            >
                                <SelectValue placeholder="Choose the area you need help with" />
                            </SelectTrigger>
                            <SelectContent className="z-[120]">
                                {GOALS.map((g) => (
                                    <SelectItem
                                        key={g}
                                        value={g}
                                        data-testid={`cmf-goal-${g}`}
                                    >
                                        {g}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <Field
                        label="Amount you'd like to borrow"
                        value={form.amount}
                        onChange={(v) => set("amount", v)}
                        testid="cmf-amount"
                        placeholder="e.g. £250,000 (optional)"
                    />

                    <div>
                        <label className="text-[11px] uppercase tracking-[0.2em] text-brand-muted font-bold">
                            When are you ready to proceed?
                        </label>
                        <Select
                            value={form.timeline}
                            onValueChange={(v) => set("timeline", v)}
                        >
                            <SelectTrigger
                                data-testid="cmf-timeline"
                                className="mt-1.5 w-full border-0 border-b border-brand-border bg-transparent rounded-none px-0 py-3 text-base text-brand-primary focus:ring-0 focus:border-brand-primary h-auto"
                            >
                                <SelectValue placeholder="Choose your timeline" />
                            </SelectTrigger>
                            <SelectContent className="z-[120]">
                                {TIMELINES.map((t) => (
                                    <SelectItem
                                        key={t.value}
                                        value={t.label}
                                        data-testid={`cmf-timeline-${t.value}`}
                                    >
                                        {t.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        data-testid="cmf-submit"
                        className="group w-full inline-flex items-center justify-center gap-2 bg-brand-primary text-white px-6 py-4 rounded-xl text-sm font-black tracking-wide hover:bg-brand-primaryHover transition-colors disabled:opacity-60"
                    >
                        {loading ? "Opening…" : "Get my free credit report"}
                        <ArrowRight
                            size={16}
                            className="transition-transform group-hover:translate-x-0.5"
                        />
                    </button>

                    <p className="text-[11px] text-brand-muted leading-relaxed">
                        By submitting your details, you confirm you have read and
                        understood our{" "}
                        <a
                            href="/privacy"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline text-brand-primary hover:text-brand-accent"
                        >
                            Privacy Policy
                        </a>
                        . An adviser will contact you about your enquiry.
                        CheckMyFile is a trusted third party — your report opens in a new tab.
                    </p>
                </form>
            </div>
        </div>
    );
}

function Field({ label, value, onChange, type = "text", testid, placeholder, autoComplete, required }) {
    return (
        <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-brand-muted font-bold">
                {label}
                {required && <span className="text-brand-accent"> *</span>}
            </label>
            <input
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testid}
                placeholder={placeholder}
                autoComplete={autoComplete}
                required={required}
                className="mt-1.5 w-full bg-transparent border-b border-brand-border py-3 text-base text-brand-primary placeholder-brand-muted focus:border-brand-primary outline-none transition-colors"
            />
        </div>
    );
}
